import type { OffsiteConfig } from "@/engine/app/offsite-config";
import { SLIDE_COMPONENTS } from "./deck";
import "./tokens.css";

export const config: OffsiteConfig = {
  id: "strategietag",
  displayName: "KI-Session Strategietag",
  subdomain: "ki.svoos.de",
  apiBaseUrl: "https://api.ki.svoos.de",
  deck: SLIDE_COMPONENTS,
  progressCuts: [],
  // Vier Phasen, wie bei der Gremien-Sitzung: kein topics/voting/grouping
  // (Solo-Teams), kein evaluating/evaluated (die Bewertung kostet Zeit ohne
  // Nutzen — der Strategietag hat pro Gruppe nur 25 Minuten).
  phases: ["intro", "einfuehrung", "working", "overnight", "reveal", "abschluss"],
  soloTeams: true,
  // Ansprache Du/ihr — am 14.09. gegen das Briefing entschieden (Viktor):
  // Folien, Seitenbildschirme und Handy sprechen durchgängig „ihr/du".
  formal: false,
  wordmark: "SVO · STRATEGIETAG",
  // Login per Einmal-Code statt Gruppen-PIN: Bei ~50 Personen je Gruppe, von
  // denen nur 5–6 auf der Bühne prompten, muss jeder Teilnehmende einzeln
  // identifizierbar sein — sonst lässt sich der fertige Prototyp am Ende nicht
  // an die richtige Person zustellen.
  codeLogin: true,
  einzelmodus: true,
  // Fünf bis sechs prompten auf der Bühne — die Folie zeigt „Bühne x/6".
  buehnenPlaetze: 6,
  identity: {
    nameLabel: "Dein voller Name",
    abteilungLabel: "Abteilung / Position",
  },
  // Die drei Felder, Reihenfolge und Hinweistexte final (Briefing Prompt-Teil,
  // Abschnitt 6). Die Schlüssel bleiben die bisherigen (thema/was/
  // anforderungen), damit Overnight-Prompt und Sanitizing unverändert greifen —
  // Feld 1 läuft über den thema-Schlüssel und wird vom gewählten Thema
  // vorbelegt, überschreibbar.
  requirementFields: [
    {
      key: "thema",
      label: "Was soll das Werkzeug sein?",
      placeholder: "Eine Liste, in der …",
      prefillFromFraming: true,
      multiline: true,
      // Dauerhaft sichtbar statt Tooltip: Für Mitarbeitende, die zum ersten
      // Mal mit einer KI arbeiten, ist die Erklärung Teil der Führung.
      hint: "Ein Satz reicht. „Eine Liste, in der …“, „Eine Seite, auf der …“",
    },
    {
      key: "was",
      label: "Was muss es können?",
      placeholder: "Es soll …",
      multiline: true,
      hint: "Drei bis fünf Dinge, die es tun soll. So, wie du es einem Kollegen erklären würdest.",
    },
    {
      key: "anforderungen",
      label: "Worauf kommt es an?",
      placeholder: "Wichtig ist mir …",
      multiline: true,
      hint: "Was dich heute nervt und morgen nicht mehr nerven soll.",
    },
  ],
  ai: { evalPromptKey: "strategietag", overnightPromptKey: "strategietag" },
  // Vorbereitete Themen: einmal hier, gelesen von der Themen-Folie und vom
  // Moderatorpult. Aus dem Arbeitsalltag von Vertrieb, Netz und Montage.
  preparedTopics: [
    {
      short: "Rechnung erklärt",
      full: "Rechnung vom Energieversorger erklärt",
    },
    {
      short: "Arbeitsbericht diktieren",
      full: "Monteur spricht nach dem Einsatz kurz rein — fertiger Arbeitsbericht",
    },
    {
      short: "Störung melden mit Foto",
      full: "Schaden abfotografieren, KI ordnet ein und legt die Meldung an",
    },
    {
      short: "Doku-Suche im Feld",
      full: "Frage vor Ort stellen, Antwort aus Arbeitsanweisungen, Plänen, Vorschriften",
    },
  ],
  // 0-basierte Slide-Indizes (siehe engine/app/offsite-config.ts), Reihenfolge
  // siehe deck.ts. Alle Folien laufen in der App, die Moderationsleiste führt
  // durch alles — kein Fensterwechsel während der Gruppe. Der SVO-OS-Exkurs
  // entfällt für den Strategietag (Briefing Prompt-Teil, Abschnitt 8) — die
  // vier Minuten Generierung tragen V1–V3.
  introSlides: 1,
  // Warten zeigt den Einstieg (Strategie 2030) — der Rahmen kommt vor der
  // Anmeldung; der QR folgt als Folie 3, die Lage steht in der Leiste.
  waitingSlide: 0,
  // Themenwahl — die Bühnengruppe entscheidet.
  themaSlide: 6,
  // Arbeitsphase — sobald die Uhr läuft, zählt die Zeit, nicht der QR-Code.
  workingSlide: 7,
  // Generierung: die Bau-Folie mit Kästchen-Fortschritt.
  overnightAnnouncementSlide: 8,
  overnightWaitLabel: "Die KI baut deinen Prototyp — die Bühne kommt auf die Leinwand, deiner bleibt unter deinem Link.",
  // Der Stations-Ablauf: acht Schritte, servergeführte Timer, Soll-Dauern
  // (Briefing Prompt-Teil, Abschnitt 3 — Summe 24:30, Puffer 0:30 zum Gong).
  stationsAblauf: {
    einfuehrungSlides: [0, 5],
    promptenSlide: 6,
    // Generierung ist der kurze Anstoß-Moment (Bau-Folie), der Vortrag trägt
    // die Inhalte — sechs Folien im Auto-Flow, während die KI weiterbaut.
    generierungSlides: [8, 8],
    vortragSlides: [9, 14],
    abschlussSlide: 15,
    phaseDauernSec: {
      einfuehrung: 180,
      thema: 120,
      prompten: 30,
      arbeitsphase: 480,
      generierung: 30,
      vortrag: 210,
      ergebnisse: 360,
      abschluss: 60,
    },
  },
  // Sprechernotizen je Folien-Index — nur in Moderationsleiste und Pult,
  // nie auf der Leinwand (Briefing Prompt-Teil, Abschnitt 5).
  sprechernotizen: {
    0: "Der Stand heißt Prozesse und Wachstum — hier der Rahmen: Bis 2030 gestalten wir die SVO durchgehend digital. Nicht als IT-Projekt, sondern damit Prozesse durchlaufen statt liegen zu bleiben.",
    1: "Zwei Hebel plus Beschleuniger: Kunden erledigen mehr selbst, intern laufen Abläufe digital durch — und die KI macht aus einer Beschreibung in Minuten einen Entwurf. Überleitung: Genau das macht ihr jetzt selbst.",
    5: "Jetzt anmelden: Publikum scannt den QR auf der Leinwand, die sechs Bühnen-Plätze ihre Karte.",
    2: "Willkommen an Station 3. Klingt nach Büro, betrifft aber jeden hier. In 25 Minuten habt ihr selbst ein Werkzeug gebaut.",
    3: "Sechs von euch zeigen es auf der Leinwand, alle anderen bekommen ihren eigenen Link. Wer noch nicht angemeldet ist: Code steht auf der Karte.",
    8: "Kurz wirken lassen: Die KI baut jetzt sechs Prototypen gleichzeitig. Dann mit Weiter in den Vortrag.",
    9: "Brücke schlagen: Was ihr gerade auf dem Handy gemacht habt, ist im Kleinen, wie wir die SVO digitalisieren — jemand beschreibt, die KI entwirft, Menschen entscheiden.",
    10: "Beispiele bringen: Zählerstand per Brief, abtippen. Baustellenzettel abends ins Büro. Die KI nimmt die grauen Glieder — nicht den Anruf, bei dem der Kunde Angst vor der Nachzahlung hat, nicht die Entscheidung am Schacht.",
    11: "Die Stunde, die beim Abtippen frei wird, geht in Beratung, PV, Glasfaser, Netzausbau. Deshalb sitzt ihr hier und nicht die IT — ihr wisst, wo die Kette hakt.",
    12: "Transparenz ist das Versprechen: Nichts von heute verschwindet. Sichten, mit dem Fachbereich prüfen, die stärksten umsetzen.",
    13: "Die Ängste offen ansprechen — genau deshalb diese Folie: keine Entscheidungen über Menschen, keine Kundendaten in Prototypen, das Gespräch bleibt bei euch.",
    14: "Aufbruch: 2030 ist das Ziel, heute war ein Anfang. Überleitung: Schauen wir, was die KI aus euren Beschreibungen gebaut hat.",
    15: "[Versprechen zum Weiterverlauf der Ideen — Text folgt von Viktor.]",
  },
  // Nur beim Reveal übernimmt die Workshop-Oberfläche die Leinwand. Während
  // des Promptens bleibt die Folie mit QR-Code und Code stehen — Nachzügler
  // finden so noch herein, und niemand sieht die Moderator-Bedienung.
  workshopOverlayPhases: ["reveal"],
};
